// src/hooks/menu/useDirectNavigation.js
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { cleanMenuFilterForQuery } from '../../util';

export const useDirectNavigation = ({
    apiSpecialsList,
    isFetchedMenuData,
    isFetchedCartData,
    menuFilter,
    retailerId,
    sort,
    cartData,
    tagSlugs,
    setSelectedSpecialId,
    setSelectedFilters,
    setQueryArgs,
    setDisplaySpecialsList,
    stableRefetchProductsData,
    filterTerms
}) => {
    const params = useParams();
    const directNavigationInitializedRef = useRef(false);

    useEffect(() => {
        if (directNavigationInitializedRef.current) return;
        if (!apiSpecialsList || !isFetchedMenuData || !isFetchedCartData) return;

        // Handle direct navigation to a specific special
        if (params?.categoryType === 'special' && params?.categoryName) {
            const specialId = params.categoryName;
            const isValidSpecial = apiSpecialsList.some(special => special.id === specialId);

            if (isValidSpecial) {
                directNavigationInitializedRef.current = true;

                // Update everything synchronously
                setSelectedSpecialId(specialId);
                const updatedFilters = { ...menuFilter, specials: true };
                setSelectedFilters(updatedFilters);

                const { sortDir, sortKey } = sort;
                const effectivePricingType = cartData?.pricingType;
                const directSpecialArgs = {
                    queryName: 'MenuBySpecialsSection',
                    variables: {
                        retailerId,
                        menuFilter: {
                            ...cleanMenuFilterForQuery(updatedFilters, menuFilter, tagSlugs?.[retailerId]),
                            menuSection: {
                                type: 'SPECIALS',
                                specialId: specialId
                            }
                        },
                        menuType: effectivePricingType,
                        sortDir,
                        sortKey
                    }
                };

                setQueryArgs(directSpecialArgs);
                setDisplaySpecialsList(true);
            }
        }
        // Handle direct navigation to specials collection WITH filters
        else if (params?.categoryType === 'specials') {
            directNavigationInitializedRef.current = true;

            let updatedFilters = { ...menuFilter, specials: true };

            // Check if we have additional filters (category/brand)
            if (params?.categoryName && params?.specialCategoryName) {
                // We're in a URL like /specials/category/pre-rolls or /specials/brand/brand-name
                if (params.categoryName === 'category') {
                    // Apply the category filter
                    updatedFilters.category = params.specialCategoryName.toUpperCase().replace(/-/g, '_');
                } else if (params.categoryName === 'brand') {
                    // Find the brand by name and apply the brand filter
                    const brandName = params.specialCategoryName.replace(/-/g, ' ');
                    const brand = filterTerms?.brands?.find(b =>
                        b.name.toLowerCase() === brandName.toLowerCase()
                    );
                    if (brand) {
                        updatedFilters.brandIds = [brand.id];
                    }
                }
            }

            setSelectedFilters(updatedFilters);

            if (apiSpecialsList?.length > 0) {
                const { sortDir, sortKey } = sort;
                const effectivePricingType = cartData?.pricingType;
                const specialsCollectionArgs = {
                    queryName: 'MenuBySpecialsSection',
                    variables: {
                        retailerId,
                        menuFilter: {
                            ...cleanMenuFilterForQuery(updatedFilters, menuFilter, tagSlugs?.[retailerId]),
                            menuSection: {
                                type: 'SPECIALS',
                                specialId: apiSpecialsList.map(special => special.id)
                            }
                        },
                        menuType: effectivePricingType,
                        sortDir,
                        sortKey
                    }
                };

                setQueryArgs(specialsCollectionArgs);
                // Only show specials list if we don't have secondary filters
                const hasSecondaryFilters = params?.specialCategoryName || params?.categoryName;
                setDisplaySpecialsList(!hasSecondaryFilters);
            }
        }
    }, [
        params,
        apiSpecialsList,
        isFetchedMenuData,
        isFetchedCartData,
        menuFilter,
        retailerId,
        sort,
        cartData?.pricingType,
        tagSlugs,
        setSelectedSpecialId,
        setSelectedFilters,
        setQueryArgs,
        setDisplaySpecialsList,
        stableRefetchProductsData
    ]);
};
